<script setup lang="ts">
// Static illustration for the "Integrations" landing section: a slice of Ava's
// page with the real embeds at their real size (TwitchEmbed live state,
// YouTubeEmbed channel + latest uploads, DiscordServerEmbed).

withDefaults(defineProps<{
  /** Drop the outer framed panel, for use inside a card that already frames it */
  bare?: boolean
}>(), { bare: false })

const videos = [
  { photo: 'yt-acoustic', title: 'Acoustic session: three new songs', views: '24K views' },
  { photo: 'yt-podcast', title: 'Podcast ep. 12 with Mia', views: '11K views' },
  { photo: 'yt-tour', title: 'Tour diary, week one', views: '8.2K views' }
]
</script>

<template>
  <div
    aria-hidden="true"
    class="flex w-full select-none flex-col pointer-events-none"
    :class="bare ? '' : 'rounded-2xl border border-default bg-elevated/30 p-4 sm:p-5 shadow-(--surface-shadow)'"
  >
    <MockPage
      bg-position="50% 30%"
      class="flex flex-1 flex-col justify-center rounded-xl p-3 font-space-grotesk sm:p-4"
    >
      <div class="grid items-start gap-3 *:min-w-0 sm:grid-cols-2">
        <!-- Twitch embed, live -->
        <div class="mock-embed">
          <div class="relative aspect-video w-full overflow-hidden">
            <MockPhoto name="twitch-stream" />
            <div class="absolute left-2 top-2 flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5">
              <span class="live-dot size-1.5 rounded-full bg-white" />
              <span class="text-[10px] font-bold uppercase tracking-wider text-white">Live</span>
            </div>
            <div class="absolute right-2 top-2 flex items-center gap-1.5">
              <span class="rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white/70">2:41:12</span>
              <span class="rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white/90">1.2K viewers</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 px-3 pb-2 pt-3">
            <span class="relative size-10 shrink-0 overflow-hidden rounded-full ring-2 ring-red-500">
              <MockPhoto
                name="avatar-ava"
                single
              />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="truncate text-sm font-semibold leading-tight text-(--mock-text-strong)">Ava</span>
                <span class="shrink-0 rounded bg-[#9146FF]/15 px-1.5 py-0.5 text-[9px] font-medium text-[#9146FF]">Partner</span>
              </div>
              <span class="block text-xs leading-tight text-(--mock-text-dim)">8.4K followers</span>
            </div>
          </div>
          <div class="px-3 pb-2">
            <p class="text-[11px] text-(--mock-text-muted)">
              Playing: <span class="text-(--mock-text)">Music</span>
            </p>
            <p class="mt-0.5 line-clamp-1 text-[11px] text-(--mock-text-dim)">
              Writing a song live with chat, come hang
            </p>
          </div>
          <div class="flex flex-wrap gap-1 px-3 pb-3">
            <span
              v-for="tag in ['English', 'Chill', 'Songwriting']"
              :key="tag"
              class="rounded bg-(--mock-tile-bg) px-1.5 py-0.5 text-[10px] text-(--mock-text-muted)"
            >{{ tag }}</span>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <!-- YouTube embed -->
          <div class="mock-embed">
            <div class="relative h-20 w-full overflow-hidden">
              <MockPhoto name="yt-banner" />
            </div>
            <div class="flex items-center gap-2.5 px-3 pb-2 pt-3">
              <span class="relative size-10 shrink-0 overflow-hidden rounded-full">
                <MockPhoto
                  name="avatar-ava"
                  single
                />
              </span>
              <div class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold leading-tight text-(--mock-text-strong)">Ava Studio</span>
                <span class="block text-xs leading-tight text-(--mock-text-dim)">
                  12.4K subscribers <span class="mx-0.5 opacity-50">&middot;</span> 214 videos
                </span>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2 px-3 pb-3">
              <div
                v-for="video in videos"
                :key="video.photo"
              >
                <div class="relative aspect-video overflow-hidden rounded-lg bg-(--mock-tile-bg)">
                  <MockPhoto :name="video.photo" />
                </div>
                <p class="mt-1 line-clamp-2 h-[2.4em] text-[11px] leading-tight text-(--mock-text-muted)">
                  {{ video.title }}
                </p>
                <span class="text-[10px] text-(--mock-text-dim)">{{ video.views }}</span>
              </div>
            </div>
          </div>

          <!-- Discord server embed -->
          <div class="mock-embed">
            <div class="h-1 w-full bg-[#5865F2]" />
            <div class="p-3">
              <div class="flex items-center gap-3">
                <span class="relative size-10 shrink-0 overflow-hidden rounded-lg">
                  <MockPhoto
                    name="avatar-ava"
                    single
                  />
                </span>
                <div class="min-w-0 flex-1">
                  <h4 class="truncate text-sm font-semibold leading-tight text-(--mock-text-strong)">
                    Ava's hangout
                  </h4>
                  <p class="mt-0.5 truncate text-xs leading-tight text-(--mock-text-dim)">
                    Listening parties, demos, and stream pings
                  </p>
                </div>
              </div>
              <div class="mt-2.5 flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <span class="flex items-center gap-1 text-xs text-(--mock-text-muted)">
                    <span class="size-2 rounded-full bg-green-500" />
                    432 Online
                  </span>
                  <span class="flex items-center gap-1 text-xs text-(--mock-text-muted)">
                    <span class="size-2 rounded-full bg-(--mock-dot)" />
                    3.1K Members
                  </span>
                </div>
                <span class="flex items-center gap-1 text-xs font-medium text-[#5865F2]">
                  Join
                  <UIcon
                    name="i-lucide-external-link"
                    class="size-3"
                  />
                </span>
              </div>
              <div class="mt-1.5 flex justify-end">
                <UIcon
                  name="i-simple-icons-discord"
                  class="size-3 text-(--mock-text-dim) opacity-50"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MockPage>
  </div>
</template>

<style scoped>
.live-dot {
  animation: live-pulse 1.6s ease-in-out infinite;
}

@keyframes live-pulse {
  0%, 100% {
    opacity: 1;
  }

  50% {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-dot {
    animation: none;
  }
}
</style>
